import React, { useReducer, useContext, useEffect } from "react";
import { priorityMetricsData } from "Data";
import { useProgress } from "Contexts/ProgressContext";

const formDataInitial = {
  userFirstName: null,
  userLastName: null,
  companyName: null,
  industry: null,
  companySize: null,
  companyMission: null,
  userJobTitle: null,
  userDepartment: null,
  primaryGoal: null,
  companyPriorities: [],
  secondaryGoals: [],
  painPoints: [],
  userEmail: null,
  userPhone: null,
  primaryMarketingChannels: [],
  primaryMarketingChannelsOther: null,
  nurturingApproach: null,
  marketingStaffing: null,
  marketingCapabilities: [],
  topMarketingGoals: [],
  marketingPainPoints: [],
  q4Score: 0,
  q7Score: 0,
  privacyConsent: false,
};

const FormDataContext = React.createContext(formDataInitial);

const formDataReducer = (state, action) => {
  let updatedState;

  const { type, payload } = action;
  switch (type) {
    case "UPDATE_USER_NAME":
      const { firstName, lastName, email } = payload;

      updatedState = {
        ...state,
        userFirstName: firstName,
        userLastName: lastName,
        userEmail: email || state.userEmail,
      };

      return updatedState;

    case "UPDATE_COMPANY_INFO":
      const { companyName, industry } = payload;

      updatedState = {
        ...state,
        companyName: companyName,
        industry: industry,
      };

      return updatedState;

    case "UPDATE_COMPANY_SIZE":
      updatedState = {
        ...state,
        companySize: payload,
      };

      return updatedState;

    case "UPDATE_COMPANY_MISSION":
      updatedState = {
        ...state,
        companyMission: payload,
      };

      return updatedState;

    case "UPDATE_JOB_INFO":
      const { title, department } = payload;

      updatedState = {
        ...state,
        userJobTitle: title,
        userDepartment: department,
      };

      return updatedState;

    case "UPDATE_PRIMARY_GOAL":
      updatedState = {
        ...state,
        primaryGoal: payload,
      };

      return updatedState;

    case "UPDATE_COMPANY_PRIORITIES":
      if (payload.length !== 3) {
        throw new Error("Secondary priorities array must contain 3 strings");
      }
      const prioritiesArray = [];
      payload.forEach((newPriorityName) => {
        let newPriorityObject = {};
        const existingPriority = state.companyPriorities.find(
          (priority) => priority.priorityName === newPriorityName,
        );

        if (existingPriority) {
          newPriorityObject = {
            ...existingPriority,
          };
        } else {
          const metric = priorityMetricsData.find(
            (item) => item.label === newPriorityName,
          );
          const { metricLabel, metricUnit } = metric;

          newPriorityObject = {
            priorityName: newPriorityName,
            metricLabel: metricLabel || "",
            metricUnit: metricUnit || null,
            metricValue: null,
            periodValue: null,
          };
        }
        prioritiesArray.push(newPriorityObject);
      });

      updatedState = {
        ...state,
        companyPriorities: prioritiesArray,
      };

      return updatedState;

    case "UPDATE_PRIORITY_METRIC":
      const { priorityName, metricValue, periodValue } = payload;
      const newPriorities = [...state.companyPriorities];
      let matchingIndex;

      let existingPriority = newPriorities.find((priority, i) => {
        let match = priority.priorityName === priorityName;
        if (match === true) {
          matchingIndex = i;
        }
        return match;
      });

      const newPriority = {
        ...existingPriority,
        metricValue: metricValue,
        periodValue: periodValue,
      };

      newPriorities[matchingIndex] = newPriority;

      updatedState = {
        ...state,
        companyPriorities: newPriorities,
      };

      return updatedState;

    case "UPDATE_USER_EMAIL_PHONE":
      const { userEmail, userPhone } = payload;

      updatedState = {
        ...state,
        userEmail: userEmail,
        userPhone: userPhone,
      };
      return updatedState;

    case "UPDATE_PAIN_POINTS":
      const newPainPoints = payload;

      updatedState = {
        ...state,
        painPoints: newPainPoints,
      };
      return updatedState;

    case "UPDATE_SECONDARY_GOALS":
      updatedState = {
        ...state,
        secondaryGoals: payload,
      };
      return updatedState;

    case "UPDATE_MARKETING_CHANNELS":
      const { channels, otherChannel, score: q4Score } = payload;
      updatedState = {
        ...state,
        primaryMarketingChannels: channels,
        primaryMarketingChannelsOther: otherChannel,
        q4Score: q4Score,
      };
      return updatedState;

    case "UPDATE_NURTURING_APPROACH":
      updatedState = {
        ...state,
        nurturingApproach: payload,
      };
      return updatedState;

    case "UPDATE_MARKETING_STAFFING":
      updatedState = {
        ...state,
        marketingStaffing: payload,
      };
      return updatedState;

    case "UPDATE_MARKETING_CAPABILITIES":
      const { capabilities, score: q7Score } = payload;
      updatedState = {
        ...state,
        marketingCapabilities: capabilities,
        q7Score: q7Score,
      };
      return updatedState;

    case "UPDATE_TOP_MARKETING_GOALS":
      updatedState = {
        ...state,
        topMarketingGoals: payload,
      };
      return updatedState;

    case "UPDATE_MARKETING_PAIN_POINTS":
      updatedState = {
        ...state,
        marketingPainPoints: payload,
      };
      return updatedState;

    case "UPDATE_PRIVACY_CONSENT":
      updatedState = {
        ...state,
        privacyConsent: payload,
      };
      return updatedState;

    case "RESTORE_FROM_SESSION":
      updatedState = {
        ...state,
        ...payload,
      };
      return updatedState;

    default:
      throw new Error(`No case for type ${type} in formDataReducer.`);
  }
};

const FormDataProvider = ({ children }) => {
  const { formComplete } = useProgress();

  const [state, dispatch] = useReducer(formDataReducer, formDataInitial);

  useEffect(() => {
    if (formComplete) {
      localStorage.removeItem("hububbleMPGsessionCode");
      return;
    }
  }, [formComplete]);

  const updateUserName = (first, last, email) => {
    dispatch({
      type: "UPDATE_USER_NAME",
      payload: {
        firstName: first,
        lastName: last,
        email: email,
      },
    });
  };

  const updateCompanyInfo = (name, industry) => {
    dispatch({
      type: "UPDATE_COMPANY_INFO",
      payload: {
        companyName: name,
        industry: industry,
      },
    });
  };

  const updateCompanySize = (size) => {
    dispatch({
      type: "UPDATE_COMPANY_SIZE",
      payload: size,
    });
  };

  const updateCompanyMission = (mission) => {
    dispatch({
      type: "UPDATE_COMPANY_MISSION",
      payload: mission,
    });
  };

  const updateJobAndDepartment = (job, dept) => {
    dispatch({
      type: "UPDATE_JOB_INFO",
      payload: {
        title: job,
        department: dept,
      },
    });
  };

  const updatePrimaryGoal = (goal) => {
    dispatch({
      type: "UPDATE_PRIMARY_GOAL",
      payload: goal,
    });
  };

  const updateCompanyPriorities = (prioritiesArray) => {
    dispatch({
      type: "UPDATE_COMPANY_PRIORITIES",
      payload: prioritiesArray,
    });
  };

  const updatePriorityMetric = (priorityName, metricValue, periodValue) => {
    dispatch({
      type: "UPDATE_PRIORITY_METRIC",
      payload: {
        priorityName,
        metricValue,
        periodValue,
      },
    });
  };

  const updateContactInfo = (email, phone) => {
    dispatch({
      type: "UPDATE_USER_EMAIL_PHONE",
      payload: {
        userEmail: email,
        userPhone: phone,
      },
    });
  };

  const updatePainPoints = (painPointsArray) => {
    dispatch({
      type: "UPDATE_PAIN_POINTS",
      payload: painPointsArray,
    });
  };

  const updateSecondaryGoals = (goalsArray) => {
    dispatch({
      type: "UPDATE_SECONDARY_GOALS",
      payload: goalsArray,
    });
  };

  const restoreFromSession = (restoredData) => {
    dispatch({
      type: "RESTORE_FROM_SESSION",
      payload: restoredData,
    });
  };

  const updateMarketingChannels = (channels, otherChannel, score) => {
    dispatch({
      type: "UPDATE_MARKETING_CHANNELS",
      payload: { channels, otherChannel, score },
    });
  };

  const updateNurturingApproach = (approach) => {
    dispatch({
      type: "UPDATE_NURTURING_APPROACH",
      payload: approach,
    });
  };

  const updateMarketingStaffing = (staffing) => {
    dispatch({
      type: "UPDATE_MARKETING_STAFFING",
      payload: staffing,
    });
  };

  const updateMarketingCapabilities = (capabilities, score) => {
    dispatch({
      type: "UPDATE_MARKETING_CAPABILITIES",
      payload: { capabilities, score },
    });
  };

  const updateTopMarketingGoals = (goals) => {
    dispatch({
      type: "UPDATE_TOP_MARKETING_GOALS",
      payload: goals,
    });
  };

  const updateMarketingPainPoints = (painPoints) => {
    dispatch({
      type: "UPDATE_MARKETING_PAIN_POINTS",
      payload: painPoints,
    });
  };

  const updatePrivacyConsent = (consent) => {
    dispatch({
      type: "UPDATE_PRIVACY_CONSENT",
      payload: consent,
    });
  };

  const providerValue = {
    currentFormData: {
      ...state,
    },
    updateUserName,
    updateCompanyInfo,
    updateCompanySize,
    updateCompanyMission,
    updateJobAndDepartment,
    updatePrimaryGoal,
    updateCompanyPriorities,
    updatePriorityMetric,
    updateContactInfo,
    updatePainPoints,
    updateSecondaryGoals,
    restoreFromSession,
    updateMarketingChannels,
    updateNurturingApproach,
    updateMarketingStaffing,
    updateMarketingCapabilities,
    updateTopMarketingGoals,
    updateMarketingPainPoints,
    updatePrivacyConsent,
  };

  return (
    <FormDataContext.Provider value={providerValue}>
      {children}
    </FormDataContext.Provider>
  );
};

const useFormData = () => {
  const context = useContext(FormDataContext);
  if (context === undefined) {
    throw new Error("useFormData must be used within FormDataContext");
  }

  return context;
};

export { formDataReducer, FormDataProvider, useFormData, FormDataContext };
